import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, IndianRupee, BarChart3, Hourglass, Wallet, CheckCircle2, UserX, UserMinus, XCircle, MessageSquareText } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { rupees } from "@lawyer/components/mobile/MobileDashboardSections";

const fmt = (n) => (n || 0).toLocaleString("en-IN");
const fmtResponse = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

// Desktop-only compact widgets around the state card. All values come from the existing dashboard payload.
export function TodaySummaryCard({ counts, todayAgenda, performance, monthPaise, fallbackPaise }) {
  const items = [
    { icon: CalendarDays, tone: "bg-blue-50 text-blue-700", value: fmt(todayAgenda.length), label: "Today's consultations", testid: "sum-today" },
    { icon: Clock, tone: "bg-indigo-50 text-indigo-700", value: fmt(counts.upcoming), label: "Upcoming consultations", testid: "sum-upcoming" },
    { icon: IndianRupee, tone: "bg-[#FBF4DF] text-[#8a6d1a]", value: `₹${rupees(monthPaise ?? fallbackPaise)}`, label: monthPaise == null ? "Total earnings" : "This month earnings", testid: "sum-earnings" },
    { icon: BarChart3, tone: "bg-emerald-50 text-emerald-700", value: `${performance.completion_rate_pct}%`, label: "Completion rate", testid: "sum-completion" },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col" data-testid="today-summary">
      <div className="flex items-center justify-between px-1">
        <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Today's Summary</p>
        <Link to={`${LAWYER_BASE}/availability`} className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1 hover:underline" data-testid="sum-calendar">View full calendar <ArrowRight size={12} /></Link>
      </div>
      <div className="mt-3 grid grid-cols-4 divide-x divide-slate-100 flex-1">
        {items.map(({ icon: Icon, tone, value, label, testid }) => (
          <div key={label} className="px-3 text-center flex flex-col items-center justify-center" data-testid={testid}>
            <div className={`h-10 w-10 rounded-full grid place-items-center ${tone}`}><Icon size={18} /></div>
            <p className="mt-2 text-xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Poppins" }}>{value}</p>
            <p className="mt-1 text-[11px] text-slate-500 leading-tight">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ icon: Icon, tone, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-t border-slate-100 first:border-t-0" data-testid={testid}>
      <span className={`h-7 w-7 rounded-md grid place-items-center ${tone}`}><Icon size={14} /></span>
      <span className="flex-1 text-sm text-slate-600">{label}</span>
      <span className="text-sm font-extrabold text-[#0A2540]">{value}</span>
    </div>
  );
}

export function EarningsSummaryCard({ earnings, monthPaise }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="earnings-summary">
      <div className="flex items-center justify-between">
        <p className="text-base font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }}><IndianRupee size={16} className="text-[#8a6d1a]" /> Earnings</p>
        <Link to={`${LAWYER_BASE}/earnings`} className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1 hover:underline" data-testid="earnings-see-all">See full breakdown <ArrowRight size={12} /></Link>
      </div>
      <div className="mt-3 rounded-xl bg-[#FBF4DF] border border-[#F1E3B5] px-4 py-3 flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-[#D4AF37] text-[#0A2540] grid place-items-center"><IndianRupee size={16} /></span>
        <div>
          <p className="text-2xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Poppins" }} data-testid="earnings-month">₹{rupees(monthPaise ?? earnings.total_earned_paise)}</p>
          <p className="text-xs text-slate-600 mt-1">{monthPaise == null ? "Total earned" : "This month"}</p>
        </div>
      </div>
      <div className="mt-2">
        <Row icon={Hourglass} tone="bg-amber-50 text-amber-700" label="Pending amount" value={`₹${rupees(earnings.pending_paise)}`} testid="earnings-pending" />
        <Row icon={Wallet} tone="bg-blue-50 text-blue-700" label="Available amount" value={`₹${rupees(earnings.available_paise)}`} testid="earnings-available" />
        <Row icon={CheckCircle2} tone="bg-emerald-50 text-emerald-700" label="Paid out amount" value={`₹${rupees(earnings.paid_out_paise)}`} testid="earnings-paid-out" />
      </div>
    </div>
  );
}

export function PerformanceSummaryCard({ performance, counts }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="performance-summary">
      <div className="flex items-center justify-between">
        <p className="text-base font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
          <BarChart3 size={16} className="text-blue-700" /> Performance <span className="text-xs font-semibold text-slate-400">(All time)</span>
        </p>
        <Link to={`${LAWYER_BASE}/consultations?bucket=completed`} className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1 hover:underline" data-testid="performance-report">View full report <ArrowRight size={12} /></Link>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-50 px-4 py-3 flex items-center gap-3" data-testid="perf-completion">
          <span className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center"><MessageSquareText size={16} /></span>
          <div><p className="text-xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Poppins" }}>{performance.completion_rate_pct}%</p><p className="text-xs text-slate-500 mt-1">Completion rate</p></div>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3 flex items-center gap-3" data-testid="perf-response">
          <span className="h-9 w-9 rounded-full bg-blue-50 text-blue-700 grid place-items-center"><Clock size={16} /></span>
          <div><p className="text-xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Poppins" }}>{fmtResponse(performance.avg_response_seconds)}</p><p className="text-xs text-slate-500 mt-1">Avg. response time</p></div>
        </div>
      </div>
      <div className="mt-2">
        <Row icon={UserX} tone="bg-rose-50 text-rose-700" label="Client no-shows" value={fmt(counts.no_show)} testid="perf-client-no-show" />
        <Row icon={UserMinus} tone="bg-amber-50 text-amber-700" label="Lawyer no-shows" value={fmt(counts.lawyer_no_show)} testid="perf-lawyer-no-show" />
        <Row icon={XCircle} tone="bg-rose-50 text-rose-700" label="Cancelled / declined" value={fmt(counts.cancelled)} testid="perf-cancelled" />
      </div>
    </div>
  );
}
